// Shots mode: the web client rendering its real screens over a faked
// network, so any screen in the match flow can be reached by URL —
// `/join/?shot=round-over` — without hosting a Match in the app, inviting a
// second player and playing seven Rounds to get there.
//
// The app's `app/src/shots/shots-mode.ts` is the same idea for the
// simulator, and the boundary is the same one: `GET /status`,
// `GET /invites/:code` and the Match socket answer from a table this tab
// holds (`table.ts`), and every screen above them is drawn by the real
// controller from what the real Engine sent. Two differences, both because
// this is a browser:
//
//   * the shot is named in the URL rather than fetched from a control
//     server. There is no `simctl openurl` confirmation to route around
//     here, and a link is the whole point.
//   * nothing is written to `localStorage`. The flow is handed a
//     `StorageLike` backed by a `Map` (`runtime.ts`), so a shot cannot
//     leave a Seat record behind that a real visit would then dial.
//
// DEV ONLY, and provably so: `import.meta.env.DEV` is replaced by `false` in
// a production build, which makes the branch below dead and takes the
// dynamic `import()` — and with it the Engine, the bots and every scenario —
// out of the bundle entirely. Nothing else in `web/src` imports this
// directory.
import type { ControllerOptions } from "../match/controller.js";

export const SHOTS_ENABLED: boolean = import.meta.env.DEV;

/** What a shot hands the page it is rendering: the seams `startMatchFlow`
 *  already declares for its own tests. `fetch` is not among them — the faked
 *  network is installed on `globalThis`, because the maintenance gate on
 *  `/` and `/play/` reaches for the browser's own. `accounts` IS among them
 *  (#223): a shot has no Accounts service to sign in against, and the
 *  session is what decides who the browser is at the table. */
export type ShotOverrides = Pick<
  ControllerOptions,
  "store" | "ports" | "accounts"
>;

export const SHOT_PARAM = "shot";

/** The shot asked for in the address, or `null` for an ordinary visit. */
export const askedShot = (search: string): string | null =>
  new URLSearchParams(search).get(SHOT_PARAM);

/**
 * Every page's entry point: `boot(renderX)` instead of `renderX()`.
 *
 * Without `?shot=` — and in every production build — this is `render()` and
 * nothing else.
 */
export const boot = (render: (overrides?: ShotOverrides) => void): void => {
  if (SHOTS_ENABLED && askedShot(globalThis.location?.search ?? "") !== null) {
    void import("./runtime.js").then((runtime) => runtime.runShot(render));
    return;
  }
  render();
};
